
import { useState } from "react";
import { toast } from "@/components/ui/use-toast";
import { signUp } from "@/services/authService";
import { supabase } from "@/integrations/supabase/client";
import { Profile } from "@/types/auth";
import { NewUserFormValues, UpdateUserFormValues } from "./schemas";

export function useUserFormSubmission(user: Profile | null, onSubmit: () => void) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (values: NewUserFormValues | UpdateUserFormValues) => {
    setIsSubmitting(true);
    
    try {
      if (user) {
        // Update existing user
        const { error } = await supabase
          .from('profiles')
          .update({
            first_name: values.first_name,
            last_name: values.last_name,
            role: values.role,
            organization_id: values.organization_id,
            is_active: values.is_active,
          })
          .eq('id', user.id);

        if (error) throw error;

        toast({
          title: "User updated",
          description: `${values.first_name} ${values.last_name} has been updated successfully.`,
        });
      } else {
        // Create new user - validate required fields
        const newUserValues = values as NewUserFormValues;
        const { email, password, first_name, last_name, role, organization_id } = newUserValues;
        
        // Validate organization requirement for organization users
        if ((role === 'organization' || role === 'organization_user') && !organization_id) {
          throw new Error("Organization users must be assigned to an organization");
        }
        
        console.log("Creating new user with data:", {
          email,
          firstName: first_name,
          lastName: last_name,
          role,
          organizationId: organization_id
        });
        
        // Create user with auto-confirmed email using edge function
        const { data: session } = await supabase.auth.getSession();
        
        if (!session?.session?.access_token) {
          throw new Error("Not authenticated");
        }

        const { data, error } = await supabase.functions.invoke('create-confirmed-user', {
          body: {
            email,
            password,
            first_name,
            last_name,
            role,
            organization_id,
          },
          headers: {
            Authorization: `Bearer ${session.session.access_token}`,
          },
        });

        if (error || !data?.success) {
          console.error("Error creating user:", error || data?.error);
          throw new Error(data?.error || error?.message || "Failed to create user");
        }

        console.log("User created successfully:", data);

        toast({
          title: "User created",
          description: `${values.first_name} ${values.last_name} can now login immediately with their credentials.`,
        });
      }
      
      onSubmit();
    } catch (error: any) {
      console.error("Form submission error:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to save user",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    isSubmitting,
    handleSubmit,
  };
}
